import React, { useLayoutEffect, useRef, useState, useEffect } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import { LICENCE_KEY } from "../../../constants/charts";

am4core.addLicense(LICENCE_KEY);

const DEFAULT_COLOR = "#fa8c16";

export default function BookingUtilisationTimelineDemo() {
  const chartDivRef = useRef(null);
  const chartRef = useRef(null);
  const dataRef = useRef(generateTimelineData());

  const [showWalkIns, setShowWalkIns] = useState(false);
  const [showNoShows, setShowNoShows] = useState(false);

  useLayoutEffect(() => {
    if (!chartDivRef.current) return;

    const chart = am4core.create(chartDivRef.current, am4charts.XYChart);

    chart.data = dataRef.current;
    chart.padding(10, 10, 0, 0);

    // ================= AXES =================

	const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
	categoryAxis.dataFields.category = "label";
	categoryAxis.renderer.minGridDistance = 40;
	categoryAxis.renderer.labels.template.fontSize = 10;
	categoryAxis.renderer.labels.template.fill = am4core.color("#666");
	categoryAxis.renderer.grid.template.strokeOpacity = 0.02;
	categoryAxis.renderer.labels.template.disabled = true;

    const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
    valueAxis.min = 0;
    valueAxis.max = 100;
    valueAxis.renderer.labels.template.fontSize = 10;
    valueAxis.renderer.labels.template.fill = am4core.color("#666");
    valueAxis.renderer.grid.template.strokeOpacity = 0.02;

    // ================= UTILISATION (🔥 SOLID COLUMNS) =================

    const utilisationSeries = chart.series.push(new am4charts.ColumnSeries());
    utilisationSeries.name = "Utilisation";
    utilisationSeries.dataFields.categoryX = "label";
    utilisationSeries.dataFields.valueY = "utilisation";

    utilisationSeries.columns.template.fill = am4core.color("#fa8c16");
    utilisationSeries.columns.template.strokeWidth = 0;
    utilisationSeries.columns.template.width = am4core.percent(90);
   utilisationSeries.columns.template.fillOpacity = showWalkIns || showNoShows ? 0.75 : 0.9;

    // 🔥 subtle rounding
    utilisationSeries.columns.template.cornerRadiusTopLeft = 3;
    utilisationSeries.columns.template.cornerRadiusTopRight = 3;

    utilisationSeries.tooltipText = "Utilisation: {valueY}";

    // ================= BOOKINGS (🔥 SEXY LINE + AREA) =================

    const bookingsSeries = chart.series.push(new am4charts.LineSeries());
    bookingsSeries.name = "Bookings";
    bookingsSeries.dataFields.categoryX = "label";
    bookingsSeries.dataFields.valueY = "bookings";

    bookingsSeries.stroke = am4core.color("darkred"); // 🔥 dark red
    bookingsSeries.strokeWidth = 2;
    bookingsSeries.tensionX = 0.4;

  

    bookingsSeries.tooltipText = "Bookings: {valueY}";

    // 🔥 glow (subtle, not tacky)
    const glow = bookingsSeries.filters.push(new am4core.DropShadowFilter());
    glow.blur = 4;
    glow.opacity = 0.2;

    // ================= WALK-INS =================

	const walkInsSeries = chart.series.push(new am4charts.ColumnSeries());
	walkInsSeries.name = "Walk-ins";
	walkInsSeries.dataFields.categoryX = "label";
	walkInsSeries.dataFields.valueY = "walkIns";

	walkInsSeries.columns.template.width = am4core.percent(50);
	walkInsSeries.columns.template.fill = am4core.color("#009900"); // nicer green
	walkInsSeries.columns.template.fillOpacity = 0.6;
	walkInsSeries.columns.template.strokeWidth = 0;

	// 👇 slight rounding so it matches style
	walkInsSeries.columns.template.cornerRadiusTopLeft = 2;
	walkInsSeries.columns.template.cornerRadiusTopRight = 2;

	// 👇 offset so it doesn’t sit dead center
	walkInsSeries.clustered = false;

	walkInsSeries.tooltipText = "Walk-ins: {valueY}";
	walkInsSeries.hide();

    // ================= NO SHOWS =================

	const noShowsSeries = chart.series.push(new am4charts.ColumnSeries());
	noShowsSeries.name = "No Shows";
	noShowsSeries.dataFields.categoryX = "label";
	noShowsSeries.dataFields.valueY = "noShows";

	noShowsSeries.columns.template.width = am4core.percent(50);
	noShowsSeries.columns.template.fill = am4core.color("#990000"); // softer red
	noShowsSeries.columns.template.fillOpacity = 0.6;
	noShowsSeries.columns.template.strokeWidth = 0;

	noShowsSeries.columns.template.cornerRadiusTopLeft = 2;
	noShowsSeries.columns.template.cornerRadiusTopRight = 2;

	noShowsSeries.clustered = false;

	noShowsSeries.tooltipText = "No Shows: {valueY}";
	noShowsSeries.hide();

    // ================= INTERACTION =================

    chart.cursor = new am4charts.XYCursor();
    chart.cursor.behavior = "panX";
    chart.cursor.lineY.disabled = true;

    chart.scrollbarX = new am4core.Scrollbar();
	chart.scrollbarX.minHeight = 6;

    // ================= LEGEND =================

    chart.legend = new am4charts.Legend();
    chart.legend.position = "top";
    chart.legend.fontSize = 11;
    chart.legend.labels.template.fill = am4core.color("#888");

    // ================= STORE =================

    chartRef.current = {
      chart,
      walkInsSeries,
      noShowsSeries
    };

    return () => {
      chart.dispose();
      chartRef.current = null;
    };

  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    const { walkInsSeries, noShowsSeries } = chartRef.current;

    showWalkIns ? walkInsSeries.show() : walkInsSeries.hide();
    showNoShows ? noShowsSeries.show() : noShowsSeries.hide();

  }, [showWalkIns, showNoShows]);

  return (
    <div style={container}>
      <div style={chartWrapper}>
        <div ref={chartDivRef} style={chartContainer} />
      </div>

      <div style={toggleColumn}>
        <Pill active={showWalkIns} onClick={() => setShowWalkIns(v => !v)}>
          Walk-ins
        </Pill>
        <Pill active={showNoShows} onClick={() => setShowNoShows(v => !v)}>
          No Shows
        </Pill>
      </div>
    </div>
  );
}

function generateTimelineData() {
  const data = [];
  const startDate = new Date(2026, 3, 1);

  for (let d = 0; d < 21; d++) {
    const current = new Date(startDate);
    current.setDate(startDate.getDate() + d);

    const day = current.getDay();

    // ❌ skip weekends
    if (day === 0 || day === 6) continue;

    for (let h = 8; h <= 18; h++) {

      // realistic daily pattern
      let base =
        h < 10 ? 30 :
        h < 12 ? 55 :
        h < 14 ? 75 :
        h < 16 ? 65 :
        45;

      // randomness
      const noise = Math.random() * 10 - 5;

      const utilisation = Math.max(10, base + noise);
	  let bookingsTrend = base + Math.sin(d * 0.8) * 10;
	  const bookings = bookingsTrend + (Math.random() * 6 - 3);

      data.push({
        label: `${d + 1}/${4} ${h}:00`,
        utilisation: Math.round(utilisation),
        bookings: Math.round(bookings),
        walkIns: Math.round(Math.random() * 10),
        noShows: Math.round(Math.random() * 6)
      });
    }
  }

  return data;
}

function Pill({ active, children, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "6px 10px",
        borderRadius: "20px",
        fontSize: "11px",
        cursor: "pointer",
        textAlign: "center",
        background: active ? "#222" : "#eee",
        color: active ? "#fff" : "#333",
        transition: "all 0.2s ease",
        userSelect: "none"
      }}
    >
      {children}
    </div>
  );
}

const container = {
  display: "flex",
  paddingLeft:'20px',
  paddingRight:'20px',
  paddingBottom:'20px',
  width: "calc(100% - 40px)",
  height: "100%",
  gap: "12px",
  alignItems: "stretch"
};

const chartWrapper = {
  flex: 9,
  minWidth: 0,
  minHeight: 320
};

const chartContainer = {
  width: "100%",
  height: 320
};

const toggleColumn = {
  flex: 1,
  minWidth: 90,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: "10px"
};